import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Cylinder, Save, Terminal, MapPin, PlayCircle, Bell } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import AtCommands from "./AtCommands";
import Areas from "./Areas";
import Simulator from "./Simulator";
import Alarms from "./Alarms";
import { CylinderTypesPanel } from "@/components/config/CylinderTypesPanel";

// Tab keys are used both for state and for `?tab=` URL sync.
const TABS = [
  { key: "cylinder",  label: "Gasify cylinder", icon: Cylinder,   testid: "cfg-tab-cylinder" },
  { key: "at",        label: "AT commands",     icon: Terminal,   testid: "cfg-tab-at" },
  { key: "areas",     label: "Areas",           icon: MapPin,     testid: "cfg-tab-areas" },
  { key: "simulator", label: "Simulator",       icon: PlayCircle, testid: "cfg-tab-simulator" },
  { key: "alarms",    label: "Alarms",          icon: Bell,       testid: "cfg-tab-alarms" },
];

export default function Configurations() {
  const [search, setSearch] = useSearchParams();
  const current = TABS.some((t) => t.key === search.get("tab")) ? search.get("tab") : "cylinder";

  const setTab = (v) => {
    const next = new URLSearchParams(search);
    next.set("tab", v);
    setSearch(next, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="configurations-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">System</div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Configurations</h1>
        <p className="text-sm text-slate-500 mt-1">Fleet-wide settings and operational modules.</p>
      </div>

      <Tabs value={current} onValueChange={setTab}>
        <TabsList
          className="w-full h-auto flex-wrap gap-1 rounded-sm bg-slate-100 p-1 justify-start"
          data-testid="cfg-tabs-list"
        >
          {TABS.map(({ key, label, icon: Icon, testid }) => (
            <TabsTrigger
              key={key}
              value={key}
              data-testid={testid}
              className="rounded-sm px-3 py-1.5 gap-2 text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm"
            >
              <Icon size={14} strokeWidth={2} />
              <span>{label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="cylinder" className="mt-4">
          <CylinderPanel />
        </TabsContent>
        <TabsContent value="at" className="mt-4">
          <div data-testid="cfg-panel-at"><AtCommands embedded /></div>
        </TabsContent>
        <TabsContent value="areas" className="mt-4">
          <div data-testid="cfg-panel-areas"><Areas embedded /></div>
        </TabsContent>
        <TabsContent value="simulator" className="mt-4">
          <div data-testid="cfg-panel-simulator"><Simulator embedded /></div>
        </TabsContent>
        <TabsContent value="alarms" className="mt-4">
          <div data-testid="cfg-panel-alarms"><Alarms embedded /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ------------------------------------------------------------------ Gasify cylinder panel
function CylinderPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [threshold, setThreshold] = useState("20");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/config");
      setCfg(data.data);
      setThreshold(String(data.data.low_cylinder_threshold_pct ?? "20"));
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to load configuration");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const threshNum = Number(threshold);
    if (threshNum < 0 || threshNum > 100) { toast.error("Low-level threshold must be 0–100 %"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/config", { low_cylinder_threshold_pct: threshNum });
      setCfg(data.data);
      toast.success("Configuration saved");
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <CylinderTypesPanel />
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="cylinder-config-card">
        <form onSubmit={save} className="p-5 space-y-6">
          <div className="max-w-xs">
            <Label className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Low-cylinder alert (%)</Label>
            <Input
              type="number" step="1" min="0" max="100"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              data-testid="config-low-threshold-input"
              className="mt-1 rounded-sm tabular-nums"
              placeholder="e.g. 20"
            />
            <div className="text-[10px] text-slate-400 mt-1">Cylinder-Level card turns flame-red below this level (amber below 50 %). Applies to all cylinder types.</div>
          </div>

        {cfg && (
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="fuel-spec-panel">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Fuel specification</div>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Composition (by mass)</div>
                <div className="mt-1 text-slate-800 font-semibold">{cfg.lpg_mix_propane_pct}% propane · {cfg.lpg_mix_butane_pct}% butane</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Liquid density</div>
                <div className="mt-1 text-slate-800 font-semibold tabular-nums">{cfg.lpg_density_kg_per_l} kg/L <span className="text-slate-400 font-normal">(@ 15 °C)</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Cylinder rating</div>
                <div className="mt-1 text-slate-800 font-semibold tabular-nums">{cfg.lpg_cylinder_max_bar} bar <span className="text-slate-400 font-normal">({(cfg.lpg_cylinder_max_bar / 10).toFixed(2)} MPa)</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Vapour pressure @ 37.8 °C</div>
                <div className="mt-1 text-slate-800 font-semibold tabular-nums">{cfg.lpg_vapour_bar_at_38c} bar <span className="text-emerald-600 font-normal">· {(cfg.lpg_cylinder_max_bar - cfg.lpg_vapour_bar_at_38c).toFixed(1)} bar cushion</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">kg → L (liquid)</div>
                <div className="mt-1 text-slate-800 font-semibold tabular-nums">1 kg ≈ {cfg.lpg_liters_per_kg} L <span className="text-slate-400 font-normal">(= 1 / {cfg.lpg_density_kg_per_l})</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">L → m³ (gas)</div>
                <div className="mt-1 text-slate-800 font-semibold tabular-nums">1 L liquid ≈ {cfg.lpg_m3_gas_per_liter} m³ gas <span className="text-slate-400 font-normal">(~{Math.round((cfg.lpg_m3_gas_per_liter || 0) * 1000)} L gas)</span></div>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">kg → m³ (gas at STP)</div>
                <div className="mt-1 text-slate-800 font-semibold tabular-nums">1 kg ≈ {cfg.lpg_m3_gas_per_kg} m³ gas <span className="text-slate-400 font-normal">&nbsp;default for new types (4.595 m³ from a 13 kg cylinder) — each type uses its own measured m³</span></div>
              </div>
            </div>
          </div>
        )}

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="text-[10px] text-slate-400" data-testid="config-last-updated">
              Last updated: {cfg?.updated_at ? new Date(cfg.updated_at).toLocaleString() : "—"}
            </div>
            <Button type="submit" disabled={saving || loading} data-testid="config-save-button" className="bg-brand-600 hover:bg-brand-700 rounded-sm">
              <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
